"use client";

import React, { useEffect, useRef, useState } from "react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedCounter({
  value,
  suffix = "",
  prefix = "",
  decimals = 0,
  isVisible
}: {
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const end = value;
    const duration = 1500;
    const frameRate = 1000 / 60;
    const totalFrames = Math.round(duration / frameRate);
    const increment = end / totalFrames;
    let currentFrame = 0;
    const timer = setInterval(() => {
      currentFrame++;
      start += increment;
      const progress = currentFrame / totalFrames;
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = end * easeOutQuart;
      if (currentFrame >= totalFrames) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(currentCount);
      }
    }, frameRate);
    return () => clearInterval(timer);
  }, [isVisible, value]);
  const formattedCount = decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString('en-US');
  return <span className="font-mono tabular-nums tracking-tighter">
            {prefix}{formattedCount}{suffix}
        </span>;
}
const generateSparklineData = (base: number, variance: number, trend: number) => {
  return Array.from({
    length: 30
  }, (_, i) => ({
    day: `Day ${i + 1}`,
    value: Math.max(0, base + (Math.random() * variance - variance / 2) + i * trend)
  }));
};
const marketStats = [{
  id: "tours",
  label: "Curated Tours",
  value: 500,
  prefix: "",
  suffix: "+",
  decimals: 0,
  data: generateSparklineData(450, 40, 1.5)
}, {
  id: "travelers",
  label: "Global Travelers",
  value: 12,
  prefix: "",
  suffix: "k",
  decimals: 0,
  data: generateSparklineData(10, 2, 0.1)
}, {
  id: "destinations",
  label: "Unique Locales",
  value: 84,
  prefix: "",
  suffix: "",
  decimals: 0,
  data: generateSparklineData(75, 8, 0.3)
}];
const tickerItems = ["IATA CERTIFIED", "GSTC SUSTAINABLE", "ASTA MEMBER", "CLIA ACCREDITED", "PATA PARTNER", "WTTC SAFE TRAVELS", "USTOA PROTECTED", "ETOA MEMBER"];
export default function RealTimeMarketPulse() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const tickerText = [...tickerItems, ...tickerItems, ...tickerItems].join(" • ");
  return <section data-section-id="222920" id="our-global-impact" className="dark bg-background relative overflow-hidden flex flex-col border-y border-muted">
            <style dangerouslySetInnerHTML={{
      __html: `
                @keyframes ticker-scroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-33.33%); }
                }
                .animate-ticker-scroll {
                    animation: ticker-scroll 40s linear infinite;
                    white-space: nowrap;
                    will-change: transform;
                }
                `
    }} />

            {}
            <div className="bg-background text-muted-foreground/40 py-4 border-b border-muted overflow-hidden flex items-center">
                <div className="flex animate-ticker-scroll font-default text-xs md:text-sm font-semibold tracking-widest uppercase grayscale opacity-60">
                    <span className="px-8">{tickerText}</span>
                    <span className="px-8">{tickerText}</span>
                    <span className="px-8">{tickerText}</span>
                </div>
            </div>

            {}
            <div ref={sectionRef} className="bg-background w-full py-12 md:py-16 lg:py-20">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-muted">
                        {marketStats.map((stat, i) => <div key={stat.id} data-index={i} className={cn("flex flex-col px-4 md:px-8 lg:px-12 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
            transitionDelay: `${i * 200}ms`
          }}>
                                <h3 className="font-default text-muted-foreground text-xs md:text-sm font-semibold uppercase tracking-widest mb-4">
                                    {stat.label}
                                </h3>
                                
                                <div className="text-6xl md:text-7xl lg:text-8xl font-default font-bold text-primary mb-8">
                                    <AnimatedCounter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} decimals={stat.decimals} isVisible={isVisible} />
                                </div>

                                <div className="h-16 md:h-20 w-full mt-auto opacity-30 grayscale">
                                    <ChartContainer config={{
                value: {
                  label: stat.label,
                  color: "var(--primary)"
                }
              }} className="h-full w-full">
                                        <LineChart data={stat.data} margin={{
                  top: 5,
                  right: 5,
                  bottom: 5,
                  left: 5
                }}>
                                            <ChartTooltip content={<ChartTooltipContent hideLabel indicator="line" />} cursor={{
                    stroke: 'var(--muted)',
                    strokeWidth: 1,
                    strokeDasharray: '4 4'
                  }} />
                                            <Line type="monotone" dataKey="value" stroke="var(--color-value)" strokeWidth={1.5} dot={false} activeDot={{
                    r: 3,
                    fill: "var(--background)",
                    stroke: "var(--color-value)",
                    strokeWidth: 1.5
                  }} isAnimationActive={isVisible} animationDuration={2000} animationEasing="ease-in-out" />
                                        </LineChart>
                                    </ChartContainer>
                                </div>
                                <div className="flex justify-between items-center mt-4 text-[10px] font-mono text-muted-foreground/50 tracking-widest">
                                    <span>ANNUAL GROWTH</span>
                                    <span className="text-primary flex items-center gap-1 font-bold">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
                                        VERIFIED
                                    </span>
                                </div>
                            </div>)}
                    </div>
                </div>
            </div>
        </section>;
}